<template>
  <div>
    <el-dialog
      v-model="isShowReferralPopup"
      class="referralModal"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :title="$t('registerUserWelcomeToReferralProgram')"
      :append-to-body="true"
      :destroy-on-close="true"
      modal-class="modalCls"
      :width="$device.isMobile ? '80%' : '50%'"
      @closed="closeReferral"
    >
      <p>{{ $t('registerUserNowYouHaveTheUniqueOpportunity') }}</p>
      <p>
        {{ $t('registerUserGetYourPersonalLink') }}
        <br />
        <span>{{
          refId &&
          `${$t('registerUserYourReferrersId')}: ${refId.toString() === HEAD_OF_SALES ? t('coreMainSystem') : refId}`
        }}</span>
        <br />
        <span>{{ $t('registerUserJoinUsAndTurnYourRecommendations') }}</span>
      </p>

      <div class="btns">
        <button
          :loading="joinLoading"
          class="btn-primary participate"
          size="large"
          :disabled="joinLoading"
          @click="registerUserWithReferral(Number(refId) || 0)"
        >
          <span class="button-content">
            <span>{{ !address ? $t('registerUserUseWalletToParticipate') : $t('registerUserParticipate') }}</span>
            <span class="right-arrow-icon" />
          </span>
        </button>
      </div>
    </el-dialog>
    <el-dialog
      v-model="isShowReferrerError"
      :title="$t('registerUserErrorInReferrerId')"
      :append-to-body="true"
      modal-class="modalCls"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      class="referralModal"
      :width="$device.isMobile ? '80%' : '50%'"
      :destroy-on-close="true"
      @closed="closeReferral"
    >
      <p>
        {{ $t('registerUserWeAreSorryButTheReferrerId') }}
        <span>{{ refId && `"${refId}" ` }}</span>
        {{ $t('registerUserInTheReferralLinkIsntValid') }}
      </p>
    </el-dialog>
    <el-dialog
      v-model="showCancelPopup"
      class="popup-canceled buy-popup"
      :class="{ 'ru-locale': $i18n.locale === 'ru' }"
      :fullscreen="$device.isMobile"
      :title="$t('referralPopupCanceledTitle')"
      :append-to-body="true"
    >
      <p>{{ $t(errorMessage) }}</p>

      <div class="btns-wrapper">
        <button class="btn-primary" size="large" @click="showCancelPopup = false">OK</button>
      </div>
    </el-dialog>
  </div>
</template>
<script setup lang="ts">
import { useWeb3Modal, useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import { ZeroAddress } from 'ethers';
import PearlApiService from '~/composables/PearlApiService';
import type { IUserModel, IUserSubscribers, UserReferralStatus } from '~/types/apiService';
import { ACTION_REJECTED, apiUrls, HEAD_OF_SALES } from '~/utils/constants';

const { t } = useI18n();

const { address, isConnected } = useWeb3ModalAccount();
const store = useMainStore();
const { open } = useWeb3Modal();
const { registerInReferral } = useStakingMethods();
const { apiUrl } = useEnvs();

const route = useRoute();
const router = useRouter();
const { isMobile } = useDevice();
const isShowReferralPopup = ref(false);
const isShowReferrerError = ref(false);
const showCancelPopup = ref(false);
const errorMessage = ref('');
const joinLoading = ref(false);
const refId = useCookie('refId');

const referrerId = computed(() => refId.value);

const { data: referralStatus } = useNuxtData<UserReferralStatus>('referralStatus');

const { data: referrerData } = useFetch<IUserModel['user']>(() => `${apiUrls.user.user}/${referrerId.value}`, {
  baseURL: apiUrl,
  watch: [referrerId],
  server: false,
  key: 'referrerData'
});

const { data: userData, status: userDataStatus } = useFetch<IUserModel>(apiUrls.v1.user, {
  baseURL: apiUrl,
  watch: [address],
  immediate: false,
  query: {
    address: address
  }
});

const { execute: updateUser } = useFetch<IUserModel>(apiUrls.v1.user, {
  method: 'PATCH',
  baseURL: apiUrl,
  body: {
    address: address,
    pendingReferrerId: refId
  },
  immediate: false,
  onResponse: async ({ response }) => {
    if (!response.ok) {
      return;
    }
    await refreshNuxtData('userData');
  }
});

const closeReferral = () => {
  isShowReferralPopup.value = false;

  if (route.path.includes('referral') && !referralStatus.value?.isReferralMember) {
    router.push('/');
  }
};

const { data: checkReferrerExists, status: checkReferrerExistsStatus } = useFetch<{ status: string; exists: boolean }>(
  apiUrls.user.exists,
  {
    query: {
      id: refId
    },
    key: 'checkReferrerExists',
    baseURL: apiUrl,
    watch: [refId, address]
  }
);

const registerHandler = async () => {
  if (!address.value) {
    return;
  }

  if (referralStatus.value?.isReferralMember) {
    ElNotification.info({
      title: '',
      message: t('registerUserAlreadyJoined')
    });

    isShowReferralPopup.value = false;

    return;
  }

  try {
    await updateUser();
    const receipt = await registerInReferral(referrerData.value?.address || ZeroAddress);

    if (!receipt?.hash) return;

    saveHashToLocalStorage(t('notificationRegisterInReferral'), receipt.hash);
    store.updateVersion();
    store.setIsJoiningToReferral(true);

    await receipt?.wait();

    if (receipt?.hash) {
      modifyHistoryHashStatus(receipt.hash, 'Done');
      store.updateVersion();

      await waitForReferralStatus();

      store.setIsJoiningToReferral(false);
    }

    const [userData, userSubscribersData] = await Promise.allSettled([
      PearlApiService.getUser(apiUrl, address.value),
      PearlApiService.getUserSubscribers(apiUrl, address.value)
    ]).then((results) => results.map((res) => (res.status === 'fulfilled' ? res.value : null)));

    if (userData) {
      store.setUserData(userData as IUserModel);
    }

    if (userSubscribersData) {
      store.setUserSubscribersData(userSubscribersData as IUserSubscribers);
    }

    joinLoading.value = false;
    isShowReferralPopup.value = false;
  } catch (err) {
    showCancelPopup.value = true;

    if ((err as { code: string })?.code === ACTION_REJECTED) {
      errorMessage.value = 'referralInvitationCancelled';
      return;
    }

    errorMessage.value = stringifyError(err);
  } finally {
    joinLoading.value = false;
  }
};

const registerUserWithReferral = (ref?: number) => {
  // address or ref is undefined
  if (!isMobile && typeof ref !== 'number') {
    return;
  }
  joinLoading.value = true;

  // connect metamask first
  if (!address.value) {
    // if no address were found, trigger wallet logic;
    open();
    return;
  }
};

async function waitForReferralStatus() {
  while (!referralStatus.value?.isReferralMember) {
    await refreshNuxtData('referralStatus');
    await new Promise((resolve) => setTimeout(resolve, 2500));
  }
}

watch(
  [address, joinLoading, isConnected, () => referrerData.value?.id, refId],
  ([newAddress, newLoader, newIsConnected]) => {
    if (newAddress && newLoader && newIsConnected && BigNumber(referrerData.value?.id || 0).eq(refId.value || 0))
      registerHandler();
  }
);

watch([() => route.query.ref, () => referralStatus.value?.isReferralMember], ([ref, isReferralMember]) => {
  if (ref && isReferralMember) {
    ElNotification.info({
      title: '',
      message: t('registerUserAlreadyJoined')
    });
    return;
  }
});

watch([() => route.query.ref, () => userData.value?.user, userDataStatus], () => {
  if (
    route.query.ref &&
    BigNumber(route.query.ref.toString()).isNaN() &&
    !userData.value?.user &&
    userDataStatus.value !== 'pending' &&
    userDataStatus.value !== 'idle'
  ) {
    ElNotification.warning({
      title: '',
      message: t('registerUserInvalidReferrer')
    });
    return;
  }
});

watch(
  [
    () => referralStatus.value?.isReferralMember,
    () => checkReferrerExists.value?.exists,
    refId,
    userDataStatus,
    address,
    () => route.name,
    () => route.query.ref,
    () => userData.value?.user
  ],
  async ([isReferralMember, referrerExists]) => {
    if (
      ((route.query.ref && !BigNumber(route.query.ref.toString()).isNaN()) || route.name === 'referral') &&
      !referrerExists &&
      userDataStatus.value !== 'pending' &&
      userDataStatus.value !== 'idle' &&
      checkReferrerExistsStatus.value !== 'pending' &&
      checkReferrerExistsStatus.value !== 'idle'
    ) {
      isShowReferrerError.value = true;
      isShowReferralPopup.value = false;
    }

    if (!referralStatus.value?.isReferralMember) {
      await refreshNuxtData('referralStatus');
    }

    if (
      (route.query.ref || route.name === 'referral' || route.path === '/') &&
      isReferralMember === false &&
      !store.isJoiningToReferral
    ) {
      isShowReferralPopup.value = true;
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.participate {
  margin-top: 20px;
}

.btn-primary:not(:disabled):hover {
  .right-arrow-icon {
    background-image: url('~/public/img/icons/login-arrow-hover.svg');
  }
}

.button-content {
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 5px;
}

.right-arrow-icon {
  display: block;
  background-image: url('~/public/img/icons/login-arrow.svg');
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  height: 12px;
  width: 15px;
  transition: background-image 0.3s ease;
}

.popup-canceled.buy-popup p {
  text-align: center;
}
</style>

<style lang="scss">
.referralModal {
  .el-dialog__body {
    color: #fff;
    text-align: center;
  }
}
</style>
